/**
 * 이미지 로드 실패 폴백 — rc의 모든 표시용 `<img>`가 거치는 한 벌.
 *
 * ## 왜 — 깨진 아이콘은 「고장」으로 읽히고 안내 문구는 「상태」로 읽힌다
 *
 * 2026-10-06 R2 정리가 **참조되던 객체 36건**을 지웠고(복구 불가) 그 결과 보고서 23건·10개 조직·
 * 결재 완료 10건이 깨진 이미지를 보고 있다. 사용자 결정은 「유실된 사진은 그대로 넘어가되 반복되지
 * 않아야 해」이고 이 컴포넌트가 그 「넘어가는」 쪽이다
 * (결정 `docs/decisions/2026-10-16-reference-set-by-column-name-missed-report-body.md`,
 *  줄기 `docs/work-streams/active/photo-storage-lifecycle.md` P6-d).
 *
 * ⚠️ **은폐가 아니다** — 깨진 아이콘이든 안내 문구든 「이미지가 없다」는 사실은 같다. 다른 것은 읽는
 * 사람이 그것을 **고장으로 읽는가 상태로 읽는가**뿐이다.
 *
 * ## 계약 넷 (어기면 조용히 깨진다)
 *
 * 1. **인라인 style만 쓴다.** rc에서만 처음 쓰는 Tailwind 유틸은 소비 앱 JIT가 생성하지 않아
 *    클래스는 붙는데 조용히 무스타일이 된다(feedback-rc-package-tailwind-class-purge-inline-style).
 * 2. **화면과 PDF가 같은 DOM이다.** rc는 2026-09-15에 `mode === 'print'` 분기를 삭제했으므로
 *    여기서 부활시키지 않는다 ⇒ **이 폴백 문구는 인쇄·PDF에도 그대로 들어간다.** 의도된 결과다.
 * 3. **발자국을 바꾸지 않는다.** 폴백 박스는 넘겨받은 `style`의 치수를 그대로 쓰고
 *    `boxSizing:'border-box'`로 테두리를 안쪽에 넣는다 — 높이가 흔들리면 `pagePlan`/`pageMeasure`의
 *    페이지 분할이 달라지고 Puppeteer 계약(`.print-ready` → `#print-meta[data-page-count]` →
 *    `#print-page-N`)이 재는 수가 바뀐다. 로드 후 자연 높이 변화는 종전처럼 `PagedComposerView`의
 *    ResizeObserver 재측정이 흡수한다.
 * 4. **`data-composer-node-id`는 폴백 박스에도 실린다.** admin Composer Studio가
 *    `closest('[data-composer-node-id]')`로 노드를 역추적하므로(primitives.tsx 헤더), 폴백 상태에서
 *    그 속성이 사라지면 **실패한 이미지 노드만 캔버스에서 선택 불가**가 된다.
 *
 * ## 성공 경로의 마크업은 종전과 **완전히 같다**
 *
 * 실패하지 않은 동안 이 컴포넌트가 내는 것은 `<img>` 하나뿐이다(래퍼 없음) — 그래서
 * `renderToStaticMarkup` 기반 기존 rc spec 전부가 영향을 받지 않는다. 상태 전이는 `onError` 뿐이고
 * effect가 없어 SSR(정적 마크업)에서도 안전하다.
 *
 * ## 실패 판정을 boolean이 아니라 **실패한 src**로 쥐는 이유
 *
 * 값이 바뀌면(개선 포인트 이미지 「다시 생성」) 다시 시도해야 한다. boolean이면 한 번 실패한 칸이
 * 새 URL을 받아도 영영 폴백에 머문다. src를 쥐면 비교만으로 재시도가 되고 effect·리셋 키가 필요 없다.
 * ([대조군] 선례 `apps/web` `OrganizationLogo`·`PhotoCardVertical`은 src가 고정이라 boolean으로 충분했다.)
 *
 * ## 서명은 이 한 벌을 쓰지 않는다
 *
 * 서명 칸(`approval/ApprovalCellContent`·`SignatureSlotView`)의 **빈 상태는 「미서명」이라는 법적
 * 의미**를 갖고 그 판정은 `resolveApprovalCellContent`가 SSOT로 소유한다 — 로드 실패를 그 밖에서
 * 문구로 바꾸면 결재 상태 표시에 소유자가 둘이 된다. print 경로는 `.signature-image` CSS 계약
 * (`approval-form.css`)도 쥐고 있다. ⚠️ 그 면제는 **「안전하다」가 아니라 「이 라운드에서 보지 않는다」**
 * 다 — 서명 값은 data URL **또는 파일 URL**이라(`SignatureSlotView` 주석) 그 자리도 깨질 수 있다.
 * 면제 선언과 분류(`OWNED_BY_APPROVAL_AXIS`)는 `__tests__/rcImageFallbackRatchet.test.ts`가 쥔다.
 * 「결재 완료본의 깨진 서명을 어떻게 보일지」는 2026-10-17 팀장이 별 항목으로 줄기에 올렸다.
 */
import { useState } from 'react';
import type { CSSProperties } from 'react';

/**
 * 폴백 칸의 보이는 문구. 접근성 이름은 `alt`를 앞에 붙여 길게 만든다(아래 `accessibleName`) —
 * 96×64 셀에도 들어가야 해서 보이는 쪽은 짧다.
 */
export const IMAGE_LOAD_FAILED_TEXT = '불러올 수 없음';

/** 폴백 칸의 시각 — `IMAGE_PLACEHOLDER_STYLE`(값 없음 칸)과 같은 톤이되 **점선**으로 구분한다.
 *  「값이 없다」와 「값은 있는데 못 불러온다」는 다른 상태라 같은 모양이면 안 된다.
 *  ⚠️ `AnnotatedImageView`에서 import하지 않는다 — 그 파일이 이 컴포넌트를 쓰므로 순환 import가 된다
 *  (재export 상수가 import 순서로만 드러나는 순환을 만든 전례가 있다). 치수는 호출부 `style`에서 오고
 *  여기 있는 것은 치수가 아닌 결뿐이라 사본 금지 규약과 충돌하지 않는다. */
const FALLBACK_BOX_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  boxSizing: 'border-box',
  padding: 2,
  background: '#f9fafb',
  color: '#9ca3af',
  fontSize: 11,
  lineHeight: 1.3,
  wordBreak: 'keep-all',
  overflow: 'hidden',
  border: '1px dashed #e5e7eb',
};

/** 높이를 정하지 못한 호출부(`height:'auto'`인 비율 유지 이미지)의 폴백 최소 높이 — 박스가 글자
 *  높이로 찌그러지면 「사진이 있던 자리」로 읽히지 않는다. */
const FALLBACK_MIN_HEIGHT = 48;

/** 치수로 셀 수 있는 값인가 — `'auto'`는 "정하지 않았다"와 같다. */
function isConcrete(v: CSSProperties['height']): boolean {
  return v !== undefined && v !== 'auto';
}

export interface ImageWithFallbackProps {
  src: string;
  /** `<img alt>` — 폴백 상태에서도 접근성 이름에 보존된다(빈 문자열이면 장식 이미지로 본다). */
  alt: string;
  style?: CSSProperties;
  /** admin 캔버스 클릭 선택 역추적용 — 폴백 박스에도 그대로 실린다(위 계약 4). */
  'data-composer-node-id'?: string;
}

/** 폴백 상태의 접근성 이름 — `alt`가 있으면 그것을 앞세운다(무엇이 안 보이는지 말해야 한다). */
function accessibleName(alt: string): string {
  return alt === '' ? `이미지 ${IMAGE_LOAD_FAILED_TEXT}` : `${alt} — ${IMAGE_LOAD_FAILED_TEXT}`;
}

/**
 * 표시용 이미지 — 로드 실패 시 같은 발자국의 안내 칸으로 바뀐다.
 * rc에서 사진·이미지를 **보여주는** 자리는 전부 이것을 쓴다(래칫이 강제).
 */
export function ImageWithFallback({
  src,
  alt,
  style,
  'data-composer-node-id': nodeId,
}: ImageWithFallbackProps) {
  // boolean이 아니라 "실패한 src" — 값이 바뀌면 자동으로 재시도한다(위 JSDoc).
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  if (failedSrc !== null && failedSrc === src) {
    const boxStyle: CSSProperties = { ...style, ...FALLBACK_BOX_STYLE };
    // 호출부 치수는 살리고 이미지 전용 속성만 벗긴다(div에서는 무의미하거나 해롭다).
    delete boxStyle.objectFit;
    delete boxStyle.objectPosition;
    if (!isConcrete(style?.height) && style?.minHeight === undefined) {
      boxStyle.minHeight = FALLBACK_MIN_HEIGHT;
    }
    return (
      <div
        data-composer-node-id={nodeId}
        data-image-fallback="true"
        role="img"
        aria-label={accessibleName(alt)}
        style={boxStyle}
      >
        {IMAGE_LOAD_FAILED_TEXT}
      </div>
    );
  }

  return (
    <img
      data-composer-node-id={nodeId}
      src={src}
      alt={alt}
      style={style}
      onError={() => setFailedSrc(src)}
    />
  );
}

export default ImageWithFallback;
